"use client";

import { useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { getImageProps } from "next/image";
import { useRouter } from "next/navigation";

function getSafeImageSrc(src) {
  if (!src) return "/img/logo.avif";

  try {
    return encodeURI(decodeURI(src));
  } catch {
    return src;
  }
}

export default function CarouselHome({
  desktopSlides = [],
  mobileSlides = [],
}) {
  const router = useRouter();

  /* ===============================
     AUTOPLAY STABLE INSTANCE
  =============================== */
  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: 4000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        playOnInit: true,
      }),
    []
  );

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: false,
      watchDrag: true,
      skipSnaps: false,
    },
    [autoplay]
  );

  /* ===============================
     PREPARE SLIDES (MEMOIZED)
  ============================== */
const slides = useMemo(() => {
  return desktopSlides
    .map((desktop, i) => ({
      desktop,
      mobile: mobileSlides[i],
    }))
    .filter((pair) => pair.desktop?.link);
}, [desktopSlides, mobileSlides]);

  if (!slides.length) return null;

  return (
    <section className="relative w-full">
      <div
        ref={emblaRef}
        className="overflow-hidden w-full"
      >
        <div className="flex">
          {slides.map(({ desktop, mobile }, index) => {
            const isFirst = index === 0;
            const alt = desktop.name || mobile?.name || "banner";
            const desktopSrc = getSafeImageSrc(desktop.link);
            const mobileSrc = getSafeImageSrc(mobile?.link || desktop.link);


            /* ===================================================
               TỐI ƯU HIỆU NĂNG GỐC (NATIVE OPTIMIZATION)
               - Chỉ tấm ảnh ĐẦU TIÊN (isFirst) được ưu tiên tải ngay (eager + high) 
                 để đạt điểm LCP tối đa trên Google Lighthouse.
               - Tất cả các tấm ảnh sau (index > 0) để mặc định 'lazy' và 'auto'.
                 Trình duyệt sẽ tự động KHÔNG tải chúng cho đến khi người dùng lướt tới gần.
            =================================================== */
            const imageOptions = {
              alt,
              quality: 80,
              sizes: "100vw",
              ...(isFirst
                ? { priority: true, fetchPriority: 'high' }
                : { loading: "lazy", fetchPriority: 'auto' }
              ),
            };

            const {
              props: { srcSet: desktopSrcSet, ...desktopImageProps },
            } = getImageProps({
              ...imageOptions,
              src: desktopSrc,
              width: 1920,
              height: 550,
            });
            const {
              props: { srcSet: mobileSrcSet },
            } = getImageProps({
              ...imageOptions,
              src: mobileSrc,
              width: 768,
              height: 550,
            });

            return (
              <div
                key={desktop.id ?? index}
                /* Giữ nguyên tỉ lệ aspect để chống giật layout (CLS = 0).
                  Thêm bg-zinc-100 làm nền mồi mặc định khi ảnh chưa kịp tải xong.
                */
                className="relative min-w-full aspect-[768/550] md:aspect-[1920/550] bg-zinc-100"
              >
                <picture>
                  {/* MOBILE IMAGE */}
                  <source
                    media="(max-width:768px)"
                    srcSet={mobileSrcSet}
                  />

                  {/* DESKTOP IMAGE */}
                  <source
                    media="(min-width:769px)"
                    srcSet={desktopSrcSet}
                  />

                  <img
                    {...desktopImageProps}
                    // Đổi 'display-block' lỗi thành 'block' chuẩn Tailwind
                    className="w-full h-full object-cover block" 
                  />
                </picture>

                {/* ===============================
                   OVERLAY (Giữ nguyên cấu trúc của bạn)
                =============================== */}
                <div
                  className="
                    absolute inset-0
                    bg-black/30
                    bottom-0
                    z-10
                    pb-8
                    md:pb-10
                    pr-4
                    pl-4
                    md:pl-8
                  "
                >
                  <h4 className="text-white text-xl md:text-4xl font-semibold">
                    {mobile?.name || desktop.name}
                  </h4>

                  <p className="text-white mt-2 text-sm md:text-lg uppercase font-semibold">
                    {mobile?.description || desktop.description}
                  </p>

                  {(mobile?.linkToPost || desktop.linkToPost) && (
                    <button
                      onClick={() =>
                        router.push(
                          mobile?.linkToPost || desktop.linkToPost
                        )
                      }
                      className="mt-3 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      aria-label="Xem chi tiết dịch vụ"
                    >
                      Chi tiết
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}